import Message from '@sats-group/ui-lib/react/message';
import TabTrapper from '@sats-group/ui-lib/react/modal/tab-trapper';
import cn from 'classnames';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useCheckoutSessionContext } from '~/client/contexts/checkout-session-context-provider';
import absoluteUrl from '~/client/helpers/absolute-url';
import { get, post } from '~/client/helpers/api-helper';
import extractServerError from '~/client/helpers/extract-server-error';
import { publish } from '~/client/helpers/messages';
import {
  type ApiResponse,
  type Core,
  type CoreConfiguration,
  useAdyen,
} from '~/client/hooks/use-adyen';
import useAsyncEffect from '~/client/hooks/use-async-effect';
import useUrlState from '~/client/hooks/use-url-state';
import { replaceQueryParameters } from '~/shared/replace-query-parameters';
import Spinner from '../spinner/spinner';
import type { Payment as Props } from './payment.types';

const magic3dsId = 'payment-3ds-frame';

const handleError = (error: Error) => {
  const message = extractServerError(error);
  if (message) {
    publish({ text: message, theme: 'error' });
  }
};

const Payment: React.FunctionComponent<Props> = ({
  adyenClientKey,
  adyenEnv,
  isMemberIdRequiredForIntegrations,
  locale,
  memberId: memberIdProp,
  messages,
  onPaymentComplete = () => {},
  onPaymentError = () => {},
  payButtonText,
  payEndpoint,
  paymentDetailsEndpoint,
  paymentErrorMessage,
  paymentMethodsEndpoint,
  paymentModuleLoadingErrorMessage,
  paymentRefusedMessage,
  returnUrl,
  sessionId: sessionIdProp,
  warning,
}) => {
  const { checkIsExpired } = useCheckoutSessionContext();
  const checkoutRef = useRef<Core>(null);
  const dropinContainer = useRef<HTMLDivElement>(null);
  const [is3ds2, setIs3ds2] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSettingAdditionDetails, setIsSettingAdditionDetails] =
    useState(false);
  const [isSettingPaymentMethods, setIsSettingPaymentMethods] = useState(false);
  const [paymentMessages, setPaymentMessages] = useState(messages);
  const [paymentMethods, setPaymentMethods] = useState();
  const [query, setUrlState] = useUrlState();

  const [{ memberId, sessionId }, setIds] = useState({
    memberId: memberIdProp,
    sessionId: sessionIdProp,
  });

  const show3ds2 = () => {
    setIs3ds2(true);
    // NOTE: Disable page scrolling:
    document.body.style.position = 'fixed';
  };

  const hide3ds2 = () => {
    document.body.style.position = '';
    setIs3ds2(false);
  };

  const [isLoadingAdyen, hasAdyenLoaded] = useAdyen(() =>
    setPaymentMessages([
      {
        text: paymentModuleLoadingErrorMessage,
        theme: 'error',
      },
    ])
  );

  const handleResponse = (
    response: ApiResponse,
    dropin: Parameters<
      NonNullable<CoreConfiguration['onAdditionalDetails']>
    >[1],
    adyenConfiguration: CoreConfiguration
  ) => {
    if (!checkoutRef.current) {
      // NOTE: This should never happen
      return;
    }

    if (response.action) {
      switch (response.action.type) {
        case 'threeDS2': {
          show3ds2();
          checkoutRef.current
            .createFromAction(response.action, {
              challengeWindowSize: '05', // NOTE: '05' is 100% * 100%
            })
            .mount(`#${magic3dsId}`);
          break;
        }
        default: {
          dropin.handleAction(response.action);
          break;
        }
      }
      return;
    }

    switch (response.resultCode) {
      case 'Authorised': {
        setUrlState({ stale: 'true' });
        onPaymentComplete(response.pspReference);
        return;
      }
      case 'Refused': {
        setPaymentMessages([{ text: paymentRefusedMessage, theme: 'error' }]);
        checkoutRef.current.update(adyenConfiguration);
        break;
      }
      default: {
        setPaymentMessages([{ text: paymentErrorMessage, theme: 'error' }]);
        checkoutRef.current.update(adyenConfiguration);
      }
    }

    hide3ds2();
    onPaymentError();
    dropin.update(dropin.props);
  };

  useEffect(() => {
    // NOTE: Props always "win" over query
    if (memberIdProp && sessionIdProp) {
      setIds({
        memberId: memberIdProp,
        sessionId: sessionIdProp,
      });
    } else if (query.memberId && query.sessionId) {
      setIds({
        memberId: String(query.memberId),
        sessionId: String(query.sessionId),
      });
    }
  }, [query, memberIdProp, sessionIdProp]);

  useEffect(() => {
    if (isMemberIdRequiredForIntegrations && !memberId) {
      return;
    }

    setIsSettingPaymentMethods(true);

    //@ts-expect-error
    get<Parameters<typeof setPaymentMethods>[0]>(
      replaceQueryParameters(paymentMethodsEndpoint, {
        memberId,
        sessionId,
      })
    )
      .then(setPaymentMethods)
      .catch(checkIsExpired)
      .catch(() => {})
      .finally(() => setIsSettingPaymentMethods(false));
  }, [isMemberIdRequiredForIntegrations, memberId, sessionId]);

  const shouldShowSpinner = useMemo(
    () =>
      isLoadingAdyen ||
      isSettingAdditionDetails ||
      isSettingPaymentMethods ||
      isSubmitting,
    [
      isLoadingAdyen,
      isSettingAdditionDetails,
      isSettingPaymentMethods,
      isSubmitting,
    ]
  );

  useAsyncEffect(async () => {
    if (
      !dropinContainer.current ||
      !hasAdyenLoaded ||
      !window.AdyenWeb ||
      !paymentMethods
    ) {
      return;
    }

    if (isMemberIdRequiredForIntegrations && !memberId) {
      return;
    }

    const translations = payButtonText
      ? {
          [`${locale}`]: {
            payButton: payButtonText,
          },
        }
      : undefined;

    const adyenConfiguration: CoreConfiguration = {
      clientKey: adyenClientKey,
      environment: adyenEnv,
      locale,
      translations,
      countryCode: locale.split('-')[1],
      onAdditionalDetails: (state, dropin) => {
        setIsSettingAdditionDetails(true);
        //@ts-expect-error
        post<ApiResponse>(paymentDetailsEndpoint, {
          memberId,
          sessionId,
          paymentDetails: state.data,
        })
          .then(response =>
            handleResponse(response, dropin, adyenConfiguration)
          )
          .catch(checkIsExpired)
          .catch(handleError)
          .finally(() => setIsSettingAdditionDetails(false));
      },
      onSubmit: (state, dropin) => {
        setIsSubmitting(true);
        setPaymentMessages([]);

        // NOTE: URLs provided by our web server are relative. Adyen needs an absolute URL.
        const redirectUrl = absoluteUrl(
          replaceQueryParameters(returnUrl, query)
        );

        //@ts-expect-error
        post<ApiResponse>(payEndpoint, {
          memberId,
          sessionId,
          originUrl: `${window.location.protocol}//${window.location.host}`,
          browserInfo: state.data.browserInfo,
          redirectUrl,
          ...state.data.paymentMethod,
        })
          .then(response =>
            handleResponse(response, dropin, adyenConfiguration)
          )
          .catch(checkIsExpired)
          .catch(handleError)
          .finally(() => setIsSubmitting(false));
      },
      paymentMethodsResponse: paymentMethods,
      removePaymentMethods: ['googlepay', 'paywithgoogle'], //note: disabling it until we switch to new pt-shop, since it's throwing config issue
    };

    const { AdyenCheckout, Dropin } = window.AdyenWeb;

    checkoutRef.current = await AdyenCheckout(adyenConfiguration);

    new Dropin(checkoutRef.current, {
      openFirstPaymentMethod: true,
      paymentMethodsConfiguration: {
        card: {
          hasHolderName: true,
          holderNameRequired: true,
          challengeWindowSize: '05',
        },
      },
      showPaymentMethods: true,
    }).mount(dropinContainer.current);

    setPaymentMessages([]);
  }, [
    hasAdyenLoaded,
    isMemberIdRequiredForIntegrations,
    memberId,
    paymentMethods,
    sessionId,
  ]);

  return (
    <div className="payment" data-adyen-client-key={adyenClientKey}>
      {shouldShowSpinner ? <Spinner theme={Spinner.themes.overlay} /> : null}
      {paymentMessages.map((message, index) => (
        <Message key={index} {...message} />
      ))}
      {warning ? (
        <div className="payment__warning">
          <Message text={warning} theme={Message.themes.warning} />
        </div>
      ) : null}
      <div ref={dropinContainer} />
      <TabTrapper isActive={is3ds2}>
        <div
          aria-modal="true"
          role="dialog"
          className={cn('payment__3ds2-wrapper', {
            'payment__3ds2-wrapper--active': is3ds2,
          })}
        >
          {shouldShowSpinner ? (
            <Spinner theme={Spinner.themes.overlay} />
          ) : null}
          <div
            className="payment__3ds2-frame"
            data-adyen-3ds2-frame
            id={magic3dsId}
          />
        </div>
      </TabTrapper>
    </div>
  );
};

export default Payment;
